import { Check, ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  alpha,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { nanoid } from 'nanoid'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import SearchInput from '~/components/searchInput'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import { EAgentKeys, EAgentStatus, TAgent } from '~/features/agents/models'
import useUserAccess from '~/features/auth/hooks/useUserAccess'
import { EClientPath } from '~/models/api'
import { ETestId } from '~/models/tests'
import rem from '~/utils/ui'

type TProps = {
  shouldShowOnSmallScreen?: boolean
}

function SelectAgent({ shouldShowOnSmallScreen = false }: TProps) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { selectedAgent, agents } = useGetAgentCentralized()
  const navigate = useNavigate()
  const [searchValue, setSearchValue] = useState('')
  const isNonMobileView = useMediaQuery(theme.breakpoints.up('sm'))
  const setAgentId = useSetAgentId()
  const { canAccessOrgConfig, canAccessAiAssistantConfig } = useUserAccess()

  // Filter to only show active assistants
  const activeAgents =
    agents?.filter((agent) => agent.agentStatus === EAgentStatus.active) || []

  const openMenuHandler = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenuHandler = () => {
    setSearchValue('')
    setAnchorEl(null)
  }

  const handleAgentChange = (agent: TAgent) => {
    setAgentId({ agentId: agent[EAgentKeys.id] })
    setSearchValue('')
  }

  const handleManageAssistantsClick = () => {
    closeMenuHandler()
    navigate(EClientPath.manageAssistants)
  }

  const handleManageBrandClick = () => {
    closeMenuHandler()
    navigate(EClientPath.manageBrand)
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const handleClearSearch = () => {
    setSearchValue('')
  }

  const filteredAgents = [...activeAgents]
    .sort((a, b) => {
      const textA = (a[EAgentKeys.agentReferenceName] || '').toLocaleLowerCase()
      const textB = (b[EAgentKeys.agentReferenceName] || '').toLocaleLowerCase()

      return textA.localeCompare(textB)
    })
    .filter((agent) =>
      (agent[EAgentKeys.agentReferenceName] || '')
        .toLocaleLowerCase()
        .includes(searchValue.toLocaleLowerCase()),
    )

  const menuContent = (
    <Box
      sx={{
        '& svg': {
          color: theme.palette.menuIcon,
          transition: 'all 0.3s ease',
        },
        '& .MuiMenuItem-root': {
          paddingInline: 4,
          paddingBlock: 1,
          lineHeight: rem(34),
          '& +.MuiDivider-root': {
            marginBottom: 0,
            marginTop: rem(8),
            backgroundColor: alpha(theme.palette.mainBackground, 0.5),
          },
          '& .MuiSvgIcon-root': {
            color: theme.palette.menuFont,
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.mainBackground, 0.2),
          },
          '&.MuiButtonBase-root.Mui-disabled.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.mainBackground, 0.2),
            '&:hover': {
              backgroundColor: alpha(theme.palette.mainBackground, 0.1),
            },
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
          },
        },
      }}
    >
      {isNonMobileView && activeAgents.length > 1 && (
        <>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              '& .MuiInputBase-root.MuiInput-root': {
                color: theme.palette.menuFont,
              },
              '& .MuiInputBase-root.MuiInput-root:before, & .MuiInputBase-root.MuiInput-root:after':
                {
                  borderBottom: `2px solid ${theme.palette.menuIcon} !important`,
                },
            }}
          >
            <SearchInput
              searchValue={searchValue}
              handleClearSearch={handleClearSearch}
              handleSearch={handleSearch}
              placeholder={
                isNonMobileView ? 'Search' : 'Search for an assistant'
              }
              style={{
                margin: 0,
                paddingInline: '1rem',
                paddingTop: 0,
                paddingBottom: '0.5rem',
              }}
              onKeyDown={(e) => {
                // Prevent menu from capturing keyboard events
                e.stopPropagation()
              }}
            />
          </Box>

          {!filteredAgents.length && (
            <MenuItem disabled>No assistants found</MenuItem>
          )}
        </>
      )}

      <Box sx={{ maxHeight: '30vh', overflow: 'auto' }}>
        {filteredAgents.map((agent) => (
          <MenuItem
            key={nanoid()}
            onClick={() => handleAgentChange(agent)}
            selected={agent[EAgentKeys.id] === selectedAgent.id}
            disabled={agent[EAgentKeys.id] === selectedAgent.id}
          >
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  flexShrink: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {agent[EAgentKeys.agentReferenceName]}
              </Box>
              {agent[EAgentKeys.id] === selectedAgent.id && (
                <IconButton edge="end" size="small">
                  <Check />
                </IconButton>
              )}
            </Stack>
          </MenuItem>
        ))}
      </Box>

      {activeAgents &&
        !!activeAgents.length &&
        (canAccessAiAssistantConfig || canAccessOrgConfig) && (
          <Divider
            variant="fullWidth"
            sx={{ background: theme.palette.menuIcon }}
          />
        )}

      {canAccessAiAssistantConfig && (
        <MenuItem
          onClick={() => handleManageAssistantsClick()}
          sx={{ padding: 0 }}
        >
          Manage Assistants
        </MenuItem>
      )}
      {canAccessOrgConfig && (
        <MenuItem onClick={() => handleManageBrandClick()} sx={{ padding: 0 }}>
          Manage Brand & Locations
        </MenuItem>
      )}
    </Box>
  )

  if (shouldShowOnSmallScreen) {
    if (isNonMobileView) {
      return null
    }

    return (
      <>
        <MenuItem sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Typography
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selectedAgent?.id
              ? `Assistant: ${
                  selectedAgent[EAgentKeys.agentReferenceName] ||
                  selectedAgent[EAgentKeys.businessName]
                }`
              : 'Select an assistant...'}
          </Typography>
        </MenuItem>
        {menuContent}
      </>
    )
  }

  return (
    <>
      <Stack
        sx={{
          display: { xxxs: 'none', sm: 'flex' },
          flexDirection: 'row',
          flexWrap: 'nowrap',
          flexGrow: 0,
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          mr: 2,
        }}
        onClick={openMenuHandler}
        aria-label="select agent"
        aria-controls="select-agent"
        aria-haspopup="true"
        data-testid={ETestId.SelectAgentButton}
      >
        <Typography
          component="span"
          sx={{
            fontSize: rem(15),
            lineHeight: rem(32),
            color: theme.palette.menuFont,
          }}
        >
          {selectedAgent?.id
            ? `Assistant: ${selectedAgent[EAgentKeys.agentReferenceName]}`
            : 'Select an assistant...'}
        </Typography>
        {anchorEl ? (
          <ExpandLess sx={{ color: theme.palette.menuFont }} />
        ) : (
          <ExpandMore sx={{ color: theme.palette.menuFont }} />
        )}
      </Stack>

      <Menu
        id="select-agent"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={Boolean(anchorEl)}
        onClick={closeMenuHandler}
        onClose={closeMenuHandler}
        TransitionProps={{
          onEntered: (element) => {
            const input = element.querySelector('input') as HTMLInputElement
            if (input) {
              input.focus()
            }
          },
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              color: theme.palette.menuFont,
              backgroundColor: theme.palette.menuBackground,
              minWidth: rem(300),
              maxWidth: '80vw',
              overflow: 'auto',
              filter: `drop-shadow(${rem(0)} ${rem(0)} ${rem(2)} ${
                theme.palette.tertiary4
              })`,
              mt: 5,
            },
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: rem(3),
          },

          '& .MuiList-root': {
            pb: 0,
          },
        }}
      >
        {menuContent}
      </Menu>
    </>
  )
}

export default SelectAgent
